"use client";

import React, { useCallback, useEffect, useState } from "react";
import { DataView } from "@/components/common/DataView";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { columns } from "./columns";
import {
  fetchWholesalerCollectionRequest,
  addWholesalerCollectionRequest,
  detachWholesalerCollectionRequest,
} from "@/store/slices/wholesalerCollection.slice";
import { useGlobalLoading } from "@/hooks/useGlobalLoading";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ButtonPrimary } from "@/components/common/ButtonPrimary";
import { ButtonOutline } from "@/components/common/ButtonOutline";
import { InputPrimary } from "@/components/common/InputPrimary";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { getAsset } from "@team-shiphouse/shiphouse-sdk";
import { formatPaise } from "@team-shiphouse/shiphouse-sdk/client";
import { Store, Package, ChevronRight } from "lucide-react";
import { RefreshCw } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export interface WholesalerCollectionFilters {
  status?: string;
  product_count_min?: number;
  product_count_max?: number;
}

const CollectionsPage: React.FC<{ id?: string; title?: string }> = ({
  id,
  title,
}) => {
  const dispatch = useAppDispatch();
  const { byWholesalerId } = useAppSelector(
    (state) => state.wholesalerCollection
  );
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [appliedFilters] = useState<WholesalerCollectionFilters>({});
  const [value, setValue] = useState<number>(0);
  const [type, setType] = useState<string>("percentage");
  const [paymentType, setPaymentType] = useState<string[]>(["PREPAID"]);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [unmapConfirmOpen, setUnmapConfirmOpen] = useState(false);
  const [collectionToUnmap, setCollectionToUnmap] = useState<any>(null);
  const { showLoading, hideLoading } = useGlobalLoading();
  const pageSize = 10;

  const { data, pagination, loading, error } = byWholesalerId?.[id || ""] || {
    data: [],
    pagination: {
      page: 0,
      total: 0,
      pageSize: 0,
      totalPages: 0,
    },
    loading: false,
    error: null,
  };

  const fetchCollections = useCallback(() => {
    if (!id) return;
    const hasSearch = searchQuery.trim();

    const requestParams: any = {
      id,
      page: pageIndex + 1,
      pageSize: pageSize,
      filters: {
        ...appliedFilters,
      },
    };

    if (hasSearch) {
      requestParams.filters.title = searchQuery;
    }

    dispatch(fetchWholesalerCollectionRequest(requestParams));
  }, [appliedFilters, searchQuery, pageIndex, dispatch, id]);

  const currentData = data || [];
  const currentPagination = {
    totalPages: pagination?.totalPages || 1,
    currentPage: (pagination?.page || 0) + 1,
    totalItems: pagination?.total || 0,
    pageSize: pagination?.pageSize || 10,
    total: pagination?.total || 0,
  };
  const currentLoading = loading;

  // Derive selectedCollections from rowSelection and currentData
  const selectedCollections = React.useMemo(() => {
    const selected: Record<string, any> = {};
    Object.keys(rowSelection).forEach((index) => {
      if (rowSelection[index] && currentData[Number(index)]) {
        const collection = currentData[Number(index)];
        selected[collection.id] = collection;
      }
    });
    return selected;
  }, [rowSelection, currentData]);

  console.log('📊 Current data:', currentData);
  console.log('📊 Data length:', currentData.length);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  useEffect(() => {
    setPageIndex(0);
    setRowSelection({}); // Clear selection when filters change
  }, [searchQuery, appliedFilters]);

  useEffect(() => {
    if (!loading && !error && Object.keys(selectedCollections).length === 0) {
      hideLoading();
    }
    if (error) {
      hideLoading();
    }
  }, [loading, error, hideLoading, selectedCollections]);

  useEffect(() => {
    console.log('🔄 selectedCollections changed:', selectedCollections);
    console.log('🔄 Number of selected:', Object.keys(selectedCollections).length);
  }, [selectedCollections]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPageIndex(0);
  };

  const handleRowClick = (collection: any) => {
    console.log('🔵 handleRowClick called with:', collection);

    // Guard against undefined collection
    if (!collection) {
      console.log('❌ Collection is undefined');
      return;
    }

    // Don't allow selection of already mapped collections
    if (collection.already_mapped) {
      console.log('❌ Collection already mapped');
      return;
    }

    const collectionId = collection.id;
    console.log('🔑 Collection ID:', collectionId);

    if (!collectionId) {
      console.log('❌ Collection ID is missing');
      return;
    }

    // Find the index of this collection in currentData
    const rowIndex = currentData.findIndex((item: any) => item.id === collectionId);

    if (rowIndex === -1) {
      console.log('❌ Collection not found in current data');
      return;
    }

    // Toggle the row selection
    setRowSelection((prev) => {
      const newSelection = { ...prev };
      if (newSelection[rowIndex]) {
        console.log('➖ Removing collection from selection');
        delete newSelection[rowIndex];
      } else {
        console.log('➕ Adding collection to selection');
        newSelection[rowIndex] = true;
      }
      console.log('📋 Updated rowSelection:', newSelection);
      return newSelection;
    });
  };

  const handlePageChange = (newPageIndex: number) => {
    setPageIndex(newPageIndex);
    setRowSelection({}); // Clear selection when page changes
  };

  const RemoveAll = () => {
    setRowSelection({});
  };

  const HandlerToAdd = () => {
    if (!id) return;

    console.log('📤 HandlerToAdd called');
    console.log('📤 selectedCollections:', selectedCollections);

    showLoading("Importing selected collections...");
    // Extract actual collection IDs from the values, filtering out any invalid ones
    const collectionIds = Object.values(selectedCollections)
      .map((collection: any) => collection?.id)
      .filter((collectionId): collectionId is string =>
        typeof collectionId === 'string' && collectionId.length > 0
      );

    console.log('📤 Extracted collection IDs:', collectionIds);

    if (collectionIds.length === 0) {
      console.log('❌ No valid collection IDs found');
      hideLoading();
      return;
    }

    console.log('📤 Dispatching addWholesalerCollectionRequest with:', {
      id: id,
      margin: value,
      margin_type: type,
      payment_type: paymentType,
      collection_ids: collectionIds,
    });

    dispatch(
      addWholesalerCollectionRequest({
        id: id,
        margin: value,
        margin_type: type,
        payment_type: paymentType,
        collection_ids: collectionIds,
      })
    );
    setRowSelection({});
  };

  const handleUnmapClick = (collection: any) => {
    setCollectionToUnmap(collection);
    setUnmapConfirmOpen(true);
  };

  const handleUnmapConfirm = () => {
    if (!id || !collectionToUnmap) return;

    dispatch(
      detachWholesalerCollectionRequest({
        id: id,
        collection_ids: [collectionToUnmap.id],
      })
    );
    setUnmapConfirmOpen(false);
    setCollectionToUnmap(null);
  };

  const handleUnmapCancel = () => {
    setUnmapConfirmOpen(false);
    setCollectionToUnmap(null);
  };

  return (
    <>
      <div className="bg-secondary-background p-[18px] border rounded-[16px]">
        <DataView
          columns={columns}
          data={currentData}
          pagination={currentPagination}
          pageIndex={pageIndex}
          onPageChange={handlePageChange}
          loading={currentLoading || false}
          error={error?.message || null}
          title={title || "Collections"}
          description="Manage collections from this wholesaler"
          searchPlaceholder="Search collections..."
          searchQuery={searchQuery}
          onSearch={handleSearch}
          onRowClick={handleRowClick}
          enableRowSelection={true}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          enableColumnResizing={true}
          emptyMessage="No collections found."
          loadingMessage="Loading collections..."
          actionButtons={[
            {
              label: "Refresh",
              icon: <RefreshCw className="h-3 w-3" />,
              onClick: fetchCollections,
              disabled: currentLoading,
              variant: "outline",
            },
          ]}
          meta={{
            onUnmap: handleUnmapClick,
          }}
        />
      </div>

      {Object.keys(selectedCollections).length > 0 && (
        <div className="flex justify-end gap-2 p-4">
          <ButtonOutline
            className="w-[212px] rounded-full border border-red-500 text-red-500 hover:bg-red-500 hover:text-primary-foreground"
            onClick={RemoveAll}
          >
            Discard
          </ButtonOutline>
          <Dialog>
            <DialogTrigger asChild>
              <ButtonPrimary
                className="w-[212px] rounded-full"
                onClick={() => {
                  console.log('🚀 Opening modal with selectedCollections:', selectedCollections);
                  console.log('📊 Number of collections:', Object.keys(selectedCollections).length);
                  console.log('📊 Collection keys:', Object.keys(selectedCollections));
                  console.log('📊 Collection values:', Object.values(selectedCollections));
                }}
              >
                Continue
              </ButtonPrimary>
            </DialogTrigger>
            <DialogContent className="p-0 overflow-hidden max-w-[1000px] bg-card max-h-[90vh] flex flex-col">
              <DialogHeader className="bg-gradient-to-r from-primary to-primary/80 px-6 py-5 flex-shrink-0">
                <DialogTitle className="text-white text-xl font-semibold">
                  Import Collections to Inventory
                </DialogTitle>
                <p className="text-white/80 text-sm mt-1">
                  Configure pricing and payment options for{" "}
                  {Object.keys(selectedCollections).length} selected
                  collection(s)
                </p>
              </DialogHeader>

              <div className="flex-1 overflow-y-auto">
                <div className="p-6 space-y-6">
                  {/* Global Margin Settings */}
                  <div className="bg-muted/30 rounded-lg p-4 border border-border/50">
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary"></span>
                      Global Margin & Payment Settings
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label
                          htmlFor="margin"
                          className="text-xs font-medium text-muted-foreground"
                        >
                          Margin Value (Applied to all collections)
                        </Label>
                        <div className="flex gap-2">
                          <InputPrimary
                            value={value}
                            onChange={(e) => setValue(Number(e.target.value))}
                            type="number"
                            className="!h-11 flex-1"
                            placeholder="Enter margin"
                          />
                          <Select value={type} onValueChange={setType}>
                            <SelectTrigger className="!bg-primary rounded-full text-white !h-11 w-[120px]">
                              <SelectValue placeholder="Type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="fixed">Flat</SelectItem>
                              <SelectItem value="percentage">%</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground">
                          Payment Methods
                        </Label>
                        <div className="flex flex-wrap gap-3 pt-1">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id="prepaid-collection"
                              checked={paymentType.includes("PREPAID")}
                              onCheckedChange={(checked) =>
                                setPaymentType((prev) =>
                                  checked
                                    ? [...prev, "PREPAID"]
                                    : prev.filter((item) => item !== "PREPAID")
                                )
                              }
                              className="w-[18px] h-[18px] rounded-sm"
                            />
                            <Label
                              htmlFor="prepaid-collection"
                              className="text-xs cursor-pointer"
                            >
                              Prepaid
                            </Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id="semi-prepaid-collection"
                              checked={paymentType.includes("SEMIPREPAID")}
                              onCheckedChange={(checked) =>
                                setPaymentType((prev) =>
                                  checked
                                    ? [...prev, "SEMIPREPAID"]
                                    : prev.filter(
                                        (item) => item !== "SEMIPREPAID"
                                      )
                                )
                              }
                              className="w-[18px] h-[18px] rounded-sm"
                            />
                            <Label
                              htmlFor="semi-prepaid-collection"
                              className="text-xs cursor-pointer"
                            >
                              Semi Prepaid
                            </Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id="cod-collection"
                              checked={paymentType.includes("POSTPAID")}
                              onCheckedChange={(checked) =>
                                setPaymentType((prev) =>
                                  checked
                                    ? [...prev, "POSTPAID"]
                                    : prev.filter((item) => item !== "POSTPAID")
                                )
                              }
                              className="w-[18px] h-[18px] rounded-sm"
                            />
                            <Label
                              htmlFor="cod-collection"
                              className="text-xs cursor-pointer"
                            >
                              COD
                            </Label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Collections List - Three Level Accordion */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary"></span>
                      Selected Collections (
                      {Object.keys(selectedCollections).length})
                    </h3>

                    {/* Helper functions for price calculations */}
                    {(() => {
                      console.log('🎨 Rendering accordion with selectedCollections:', selectedCollections);
                      console.log('🎨 Selected collection entries:', Object.entries(selectedCollections));

                      const calculateNewPrice = (originalPrice: number, margin: number, marginType: string) => {
                        if (marginType === "percentage") {
                          return originalPrice + (originalPrice * margin / 100);
                        } else {
                          return originalPrice + (margin * 100);
                        }
                      };

                      const calculateProfit = (originalPrice: number, newPrice: number) => {
                        return newPrice - originalPrice;
                      };

                      const getVariantsFromProduct = (product: any) => {
                        if (product.variants && product.variants.length > 0) {
                          return product.variants;
                        }
                        return [{
                          id: product.id,
                          title: "Default",
                          price: product.price || 0,
                          sku: product.sku || "",
                        }];
                      };

                      const calculateProductProfit = (product: any) => {
                        const variants = getVariantsFromProduct(product);
                        return variants.reduce((sum: number, variant: any) => {
                          const originalPrice = variant.price || 0;
                          const newPrice = calculateNewPrice(originalPrice, value, type);
                          return sum + calculateProfit(originalPrice, newPrice);
                        }, 0);
                      };

                      const calculateCollectionProfit = (collection: any) => {
                        if (!collection.products || !Array.isArray(collection.products)) {
                          return 0;
                        }
                        return collection.products.reduce((sum: number, product: any) => {
                          return sum + calculateProductProfit(product);
                        }, 0);
                      };

                      const getTotalVariantsCount = (collection: any) => {
                        if (!collection.products || !Array.isArray(collection.products)) {
                          return 0;
                        }
                        return collection.products.reduce((count: number, product: any) => {
                          const variants = getVariantsFromProduct(product);
                          return count + variants.length;
                        }, 0);
                      };

                      return (
                        <Accordion type="multiple" className="space-y-3">
                          {Object.entries(selectedCollections).map(
                            ([key, collection]) => {
                              console.log(`🗂️ Rendering collection key: ${key}`, collection);

                              // Use total_products if available, otherwise fallback to products array
                              const productCount = collection.total_products !== undefined
                                ? collection.total_products
                                : (Array.isArray(collection.products) ? collection.products.length : 0);
                              console.log(`📦 Collection "${collection.title}" has ${productCount} products`);

                              const totalVariants = getTotalVariantsCount(collection);
                              const collectionProfit = calculateCollectionProfit(collection);

                              console.log(`💰 Collection "${collection.title}" profit: ${collectionProfit}`);

                              return (
                                <div
                                  key={key}
                                  className="border border-border/50 rounded-lg overflow-hidden bg-background"
                                >
                                  {/* Level 1: Collection Accordion */}
                                  <AccordionItem value={key} className="border-0">
                                    <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/30 transition-colors bg-gradient-to-br from-primary/10 to-primary/5">
                                      <div className="flex items-center justify-between w-full pr-2">
                                        <div className="flex items-center gap-4">
                                          <div className="w-14 h-14 overflow-hidden rounded-lg flex-shrink-0 bg-muted">
                                            {collection.image ? (
                                              <img
                                                src={getAsset(collection.image, {
                                                  width: 80,
                                                  height: 80,
                                                  fit: "cover",
                                                })}
                                                alt={collection.title}
                                                className="w-14 h-14 object-cover"
                                                onError={(e) => {
                                                  (e.target as HTMLImageElement).src = getAsset(
                                                    "seller/b66f0c3b-54dc-4564-af3a-848196817901/products/images/bljmuedorxDSsvOtpgQP.jpg",
                                                    { width: 80, height: 80, fit: "cover" }
                                                  );
                                                }}
                                              />
                                            ) : (
                                              <div className="w-14 h-14 flex items-center justify-center">
                                                <Store className="h-6 w-6 text-muted-foreground" />
                                              </div>
                                            )}
                                          </div>
                                          <div className="text-left">
                                            <h4 className="font-semibold text-sm">{collection.title}</h4>
                                            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                                              <span>{productCount} product{productCount !== 1 ? "s" : ""}</span>
                                              <span>•</span>
                                              <span>{totalVariants} variant{totalVariants !== 1 ? "s" : ""}</span>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="text-right">
                                          <p className="text-xs text-muted-foreground">Total Profit</p>
                                          <p className="font-semibold text-green-600">{formatPaise(collectionProfit)}</p>
                                        </div>
                                      </div>
                                    </AccordionTrigger>
                                    <AccordionContent className="p-0">
                                      {/* Level 2: Products within Collection */}
                                      <div className="border-t border-border/30">
                                        <Accordion type="multiple" className="divide-y divide-border/30">
                                          {(collection.products || []).map((product: any, productIndex: number) => {
                                            const variants = getVariantsFromProduct(product);
                                            const productProfit = calculateProductProfit(product);

                                            return (
                                              <AccordionItem
                                                key={product.id || productIndex}
                                                value={`${key}-product-${product.id || productIndex}`}
                                                className="border-0"
                                              >
                                                <AccordionTrigger className="px-4 py-3 pl-8 hover:no-underline hover:bg-muted/20 transition-colors">
                                                  <div className="flex items-center justify-between w-full pr-2">
                                                    <div className="flex items-center gap-3">
                                                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                                      <div className="w-12 h-12 rounded-md overflow-hidden bg-muted flex-shrink-0">
                                                        {product.images && product.images.length > 0 ? (
                                                          <img
                                                            src={getAsset(product.images[0], { width: 80, height: 80, fit: 'cover' })}
                                                            alt={product.title}
                                                            className="w-full h-full object-cover"
                                                          />
                                                        ) : (
                                                          <div className="w-full h-full flex items-center justify-center">
                                                            <Package className="h-5 w-5 text-muted-foreground" />
                                                          </div>
                                                        )}
                                                      </div>
                                                      <div className="text-left">
                                                        <p className="font-medium text-sm">{product.title}</p>
                                                        <p className="text-xs text-muted-foreground mt-0.5">
                                                          {variants.length} variant{variants.length !== 1 ? "s" : ""}
                                                        </p>
                                                      </div>
                                                    </div>
                                                    <div className="text-right">
                                                      <p className="text-xs text-muted-foreground">Profit</p>
                                                      <p className="font-semibold text-green-600 text-sm">{formatPaise(productProfit)}</p>
                                                    </div>
                                                  </div>
                                                </AccordionTrigger>
                                                <AccordionContent className="p-0">
                                                  {/* Level 3: Variants Table */}
                                                  <div className="bg-muted/10 border-t border-border/20">
                                                    <Table>
                                                      <TableHeader>
                                                        <TableRow className="bg-muted/30 hover:bg-muted/30">
                                                          <TableHead className="font-semibold text-xs w-[60px] pl-12">Image</TableHead>
                                                          <TableHead className="font-semibold text-xs">Variant</TableHead>
                                                          <TableHead className="font-semibold text-xs w-[120px]">Original Price</TableHead>
                                                          <TableHead className="font-semibold text-xs w-[120px]">New Price</TableHead>
                                                          <TableHead className="font-semibold text-xs w-[120px]">Profit</TableHead>
                                                        </TableRow>
                                                      </TableHeader>
                                                      <TableBody>
                                                        {variants.map((variant: any, variantIndex: number) => {
                                                          const originalPrice = variant.price || 0;
                                                          const newPrice = calculateNewPrice(originalPrice, value, type);
                                                          const profit = calculateProfit(originalPrice, newPrice);

                                                          return (
                                                            <TableRow
                                                              key={variant.id || variantIndex}
                                                              className="hover:bg-muted/20"
                                                            >
                                                              <TableCell className="py-2 pl-12">
                                                                <div className="w-10 h-10 rounded-md overflow-hidden bg-muted">
                                                                  {variant.image ? (
                                                                    <img
                                                                      src={getAsset(variant.image, { width: 60, height: 60, fit: 'cover' })}
                                                                      alt={variant.title}
                                                                      className="w-full h-full object-cover"
                                                                    />
                                                                  ) : product.images && product.images.length > 0 ? (
                                                                    <img
                                                                      src={getAsset(product.images[0], { width: 60, height: 60, fit: 'cover' })}
                                                                      alt={variant.title}
                                                                      className="w-full h-full object-cover"
                                                                    />
                                                                  ) : (
                                                                    <div className="w-full h-full flex items-center justify-center">
                                                                      <Package className="h-4 w-4 text-muted-foreground" />
                                                                    </div>
                                                                  )}
                                                                </div>
                                                              </TableCell>
                                                              <TableCell className="py-2">
                                                                <div>
                                                                  <p className="font-medium text-sm">{variant.title}</p>
                                                                  {variant.sku && (
                                                                    <p className="text-xs text-muted-foreground">SKU: {variant.sku}</p>
                                                                  )}
                                                                </div>
                                                              </TableCell>
                                                              <TableCell className="py-2">
                                                                <span className="font-medium text-sm">{formatPaise(originalPrice)}</span>
                                                              </TableCell>
                                                              <TableCell className="py-2">
                                                                <span className="font-semibold text-sm text-primary">{formatPaise(newPrice)}</span>
                                                              </TableCell>
                                                              <TableCell className="py-2">
                                                                <span className="font-semibold text-sm text-green-600">{formatPaise(profit)}</span>
                                                              </TableCell>
                                                            </TableRow>
                                                          );
                                                        })}
                                                      </TableBody>
                                                    </Table>
                                                  </div>
                                                </AccordionContent>
                                              </AccordionItem>
                                            );
                                          })}
                                        </Accordion>
                                      </div>
                                    </AccordionContent>
                                  </AccordionItem>
                                </div>
                              );
                            }
                          )}
                        </Accordion>
                      );
                    })()}
                  </div>
                </div>
              </div>

              <DialogFooter className="justify-between p-6 pt-4 border-t border-border/50 flex-shrink-0 bg-muted/20 flex flex-row gap-3">
                <div className="flex flex-col gap-1">
                  <div className="text-sm text-muted-foreground">
                    {Object.keys(selectedCollections).length} collection(s) will be imported
                  </div>
                  {(() => {
                    const calculateNewPrice = (originalPrice: number, margin: number, marginType: string) => {
                      if (marginType === "percentage") {
                        return originalPrice + (originalPrice * margin / 100);
                      } else {
                        return originalPrice + (margin * 100);
                      }
                    };

                    let totalProfit = 0;
                    let hasProducts = false;
                    Object.values(selectedCollections).forEach((collection: any) => {
                      if (collection.products && Array.isArray(collection.products) && collection.products.length > 0) {
                        hasProducts = true;
                        collection.products.forEach((product: any) => {
                          const variants = product.variants && product.variants.length > 0
                            ? product.variants
                            : [{ price: product.price || 0 }];
                          variants.forEach((variant: any) => {
                            const originalPrice = variant.price || 0;
                            const newPrice = calculateNewPrice(originalPrice, value, type);
                            totalProfit += newPrice - originalPrice;
                          });
                        });
                      }
                    });

                    return hasProducts ? (
                      <div className="text-sm">
                        Total Profit:{" "}
                        <span className="font-semibold text-green-600">{formatPaise(totalProfit)}</span>
                      </div>
                    ) : (
                      <div className="text-sm">
                        Margin:{" "}
                        <span className="font-semibold text-primary">
                          {type === "percentage" ? `${value}%` : `₹${value}`}
                        </span>
                      </div>
                    );
                  })()}
                </div>
                <div className="flex gap-3">
                  <DialogTrigger asChild>
                    <ButtonOutline
                      type="button"
                      className="px-6 rounded-lg border-2 hover:bg-muted"
                    >
                      Cancel
                    </ButtonOutline>
                  </DialogTrigger>
                  <ButtonPrimary
                    type="button"
                    className="px-6 rounded-lg"
                    onClick={HandlerToAdd}
                  >
                    Import Collections
                  </ButtonPrimary>
                </div>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Unmap Confirmation Dialog */}
      <Dialog open={unmapConfirmOpen} onOpenChange={setUnmapConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Unmap Collection</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to unmap the collection{" "}
              <span className="font-semibold text-foreground">
                "{collectionToUnmap?.title}"
              </span>
              ? This will remove it from your inventory.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <ButtonOutline onClick={handleUnmapCancel}>
              Cancel
            </ButtonOutline>
            <ButtonPrimary
              onClick={handleUnmapConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Unmap Collection
            </ButtonPrimary>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CollectionsPage;
